import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useCompleteProfile } from "@/hooks/useCompleteProfile";
import { toast } from "@/hooks/use-toast";
import { ADVISOR_PRIMARY_GOALS } from "@/constants/advisor-goals";
import { supabase } from "@/integrations/supabase/client";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { caProvinces as PROVINCES } from "@/components/profile/address/addressConstants";

const BUSINESS_STRUCTURES = [
  { value: "sole_proprietorship", label: "Sole Proprietorship" },
  { value: "partnership", label: "Partnership" },
  { value: "corporation", label: "Corporation" },
];

const REGULATORY_LICENSES = [
  { value: "IIROC/CIRO-Licensed Advisor", label: "IIROC/CIRO-Licensed Advisor" },
  { value: "MFDA/CIRO-Licensed Advisor", label: "MFDA/CIRO-Licensed Advisor" },
  { value: "Other", label: "Other" },
];

const ADVISORY_ROLES = [
  { value: "Portfolio Manager", label: "Portfolio Manager" },
  { value: "Financial Planner", label: "Financial Planner" },
  { value: "Investment Advisor / Broker", label: "Investment Advisor / Broker" },
  { value: "Insurance Representative", label: "Insurance Representative" },
  { value: "Wealth Management Advisor", label: "Wealth Management Advisor" },
  { value: "Other", label: "Other" },
];

type Screen = "welcome" | "name" | "role" | "practice" | "details" | "credentials" | "goals" | "complete";

interface FormData {
  firstName: string;
  lastName: string;
  businessStructure: string;
  province: string;
  practiceName: string;
  registrationNumber: string;
  streetAddress: string;
  city: string;
  addressProvince: string;
  postalCode: string;
  website: string;
  primaryDealer: string;
  regulatoryLicense: string;
  advisoryRole: string;
  otherRoleText: string;
  goals: string[];
  otherGoalText: string;
}

interface FirmAffiliation {
  name: string;
  category: string;
}

export default function CompleteProfile() {
  const [screen, setScreen] = useState<Screen>("welcome");
  const [submitting, setSubmitting] = useState(false);
  const [firmAffiliations, setFirmAffiliations] = useState<FirmAffiliation[]>([]);
  const [firmSearchOpen, setFirmSearchOpen] = useState(false);
  const [firmSearchValue, setFirmSearchValue] = useState("");
  const [registrationError, setRegistrationError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    businessStructure: "",
    province: "",
    practiceName: "",
    registrationNumber: "",
    streetAddress: "",
    city: "",
    addressProvince: "",
    postalCode: "",
    website: "",
    primaryDealer: "",
    regulatoryLicense: "",
    advisoryRole: "",
    otherRoleText: "",
    goals: [],
    otherGoalText: "",
  });

  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const completeProfile = useCompleteProfile();

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.email_confirmed_at) {
      toast({
        title: "Email verification required",
        description: "Please verify your email before completing your profile.",
        variant: "destructive",
      });
      navigate("/register/verify");
      return;
    }
  }, [user, navigate]);

  // Load firm affiliations via RPC (platform_settings is admin-only)
  useEffect(() => {
    async function loadFirmAffiliations() {
      try {
        const { data, error } = await supabase.rpc('get_firm_affiliations');

        if (!error && data) {
          const parsed = typeof data === 'string' ? JSON.parse(data) : data;
          const categories = parsed?.categories ?? (Array.isArray(parsed) ? parsed : []);
          // Flatten nested { label, firms[] } structure into { name, category } objects
          const flattened: FirmAffiliation[] = [];
          for (const cat of categories) {
            const categoryLabel = cat.label || cat.category || 'Other';
            const firms = cat.firms || [];
            for (const firm of firms) {
              if (typeof firm === 'string') {
                flattened.push({ name: firm, category: categoryLabel });
              } else if (firm?.name) {
                flattened.push({ name: firm.name, category: firm.category || categoryLabel });
              }
            }
          }
          setFirmAffiliations(flattened);
        }
      } catch (e) {
        console.warn('Failed to load firm affiliations:', e);
      }
    }
    loadFirmAffiliations();
  }, []);

  const screens: Screen[] = ["welcome", "name", "role", "practice", "details", "credentials", "goals", "complete"];
  const stepScreens: Screen[] = ["name", "role", "practice", "details", "credentials", "goals"];
  const currentStepIndex = stepScreens.indexOf(screen);
  const stepNumber = currentStepIndex + 1;

  const stepLabels: Record<string, string> = {
    name: "Your Name",
    role: "Your Role",
    practice: "Your Practice",
    details: "Practice Details",
    credentials: "Your Credentials",
    goals: "Your Goals",
  };

  const goTo = (s: Screen) => setScreen(s);
  const goBack = () => {
    const idx = screens.indexOf(screen);
    if (idx > 0) setScreen(screens[idx - 1]);
  };
  const goNext = () => {
    const idx = screens.indexOf(screen);
    if (idx < screens.length - 1) setScreen(screens[idx + 1]);
  };

  // Validate registration number: exactly 7 or 10 digits
  const validateRegistrationNumber = (value: string): boolean => {
    if (!value.trim()) return true; // optional
    const digits = value.replace(/\D/g, "");
    return digits.length === 7 || digits.length === 10;
  };

  // Validate Canadian postal code: A1A 1A1
  const validatePostalCode = (value: string): boolean => {
    if (!value.trim()) return true;
    return /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/.test(value.trim());
  };

  const canProceed = (): boolean => {
    switch (screen) {
      case "name":
        return formData.firstName.trim() !== "" && formData.lastName.trim() !== "";
      case "role":
        return true;
      case "practice":
        return formData.businessStructure !== "" && formData.province !== "";
      case "details":
        // Practice name is required. Registration number validation if corporation.
        if (formData.practiceName.trim() === "") return false;
        if (formData.businessStructure === "corporation" && formData.registrationNumber.trim() !== "") {
          if (!validateRegistrationNumber(formData.registrationNumber)) return false;
        }
        if (formData.postalCode.trim() !== "" && !validatePostalCode(formData.postalCode)) return false;
        return true;
      case "credentials":
        return formData.regulatoryLicense !== "" && formData.advisoryRole !== "" && (formData.advisoryRole !== "Other" || formData.otherRoleText.trim() !== "");
      case "goals":
        return formData.goals.length > 0;
      default:
        return true;
    }
  };

  const handleGoalChange = (goal: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      goals: checked ? [...prev.goals, goal] : prev.goals.filter((g) => g !== goal),
      ...(goal === "Other" && !checked ? { otherGoalText: "" } : {}),
    }));
  };

  // Filter firm affiliations by search
  const filteredAffiliations = useMemo(() => {
    if (!firmSearchValue.trim()) return firmAffiliations;
    const lower = firmSearchValue.toLowerCase();
    return firmAffiliations.filter(f => f.name.toLowerCase().includes(lower));
  }, [firmAffiliations, firmSearchValue]);

  // Group affiliations by category
  const groupedAffiliations = useMemo(() => {
    const groups: Record<string, FirmAffiliation[]> = {};
    filteredAffiliations.forEach(f => {
      const cat = f.category || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(f);
    });
    return groups;
  }, [filteredAffiliations]);

  const handleSubmit = async () => {
    if (!user) return;
    setSubmitting(true);

    try {
      const finalGoals = formData.goals.map((g) => {
        if (g === "Other" && formData.otherGoalText.trim()) {
          return `Other: ${formData.otherGoalText.trim()}`;
        }
        return g;
      });

      // Build business address jsonb
      const businessAddress = (formData.streetAddress.trim() || formData.city.trim() || formData.addressProvince || formData.postalCode.trim())
        ? {
            street: formData.streetAddress.trim(),
            city: formData.city.trim(),
            province: formData.addressProvince,
            postal_code: (() => {
              const pc = formData.postalCode.trim().toUpperCase().replace(/\s/g, '');
              return pc.length === 6 ? `${pc.slice(0, 3)} ${pc.slice(3)}` : formData.postalCode.trim().toUpperCase();
            })(),
          }
        : null;

      console.log('Submitting onboarding data:', {
        role: 'business_owner (hardcoded)',
        firstName: formData.firstName,
        lastName: formData.lastName,
        practiceName: formData.practiceName,
        businessStructure: formData.businessStructure,
        province: formData.province,
        regulatoryLicense: formData.regulatoryLicense,
        advisoryRole: formData.advisoryRole,
        primaryDealer: formData.primaryDealer,
        businessAddress,
        registrationNumber: formData.registrationNumber,
        website: formData.website,
        goals: formData.goals,
        finalGoals,
      });

      const result = await completeProfile.mutateAsync({
        role: "business_owner",
        legal_first_name: formData.firstName.trim(),
        legal_last_name: formData.lastName.trim(),
        company_name: formData.practiceName.trim() || undefined,
        business_structure: formData.businessStructure || undefined,
        industry: undefined,
        goals: finalGoals.length > 0 ? finalGoals : undefined,
        hq_location_province: formData.province || undefined,
        primary_license: formData.regulatoryLicense || undefined,
        advisor_role: formData.advisoryRole === "Other" ? formData.otherRoleText.trim() : formData.advisoryRole || undefined,
        primary_dealer: formData.primaryDealer.trim() || undefined,
        business_address: businessAddress || undefined,
        business_registration_number: formData.registrationNumber.trim() || undefined,
        website: formData.website.trim() || undefined,
      });

      console.log('RPC result:', result);

      // Fire-and-forget admin notification email
      const sendAdminNotification = async (
        firstName: string,
        lastName: string,
        email: string
      ) => {
        try {
          await supabase.functions.invoke('send-email', {
            body: {
              to: 'info@mysuccession.io',
              subject: `New Account Created — ${firstName} ${lastName}`,
              html: `
                <html>
                  <head>
                    <style>body{font-family:Arial,sans-serif;margin:0;padding:0;background:#f4f4f4}table{border-collapse:collapse}.container{max-width:600px;margin:20px auto;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 4px rgba(0,0,0,.1)}.header{background:#1a1a2e;color:#fff;padding:24px;text-align:center}.content{padding:24px}.info-table{width:100%;margin:16px 0}.info-table td{padding:10px 12px;border-bottom:1px solid #eee}.info-table td:first-child{font-weight:600;color:#555;width:140px}.footer{text-align:center;padding:16px;color:#999;font-size:12px;border-top:1px solid #eee}</style>
                  </head>
                  <body>
                    <div class="container">
                      <div class="header">
                        <h2 style="margin:0">New Account Registration</h2>
                      </div>
                      <div class="content">
                        <p style="color:#333;line-height:1.6">
                          A new user has completed onboarding on mySuccession.
                        </p>
                        <table class="info-table">
                          <tr>
                            <td>First Name</td>
                            <td>${firstName}</td>
                          </tr>
                          <tr>
                            <td>Last Name</td>
                            <td>${lastName}</td>
                          </tr>
                          <tr>
                            <td>Email</td>
                            <td>${email}</td>
                          </tr>
                          <tr>
                            <td>Registered</td>
                            <td>${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          </tr>
                        </table>
                        <hr style="border:none;border-top:1px solid #eee;margin:24px 0" />
                        <p style="color:#999;font-size:12px;text-align:center">
                          This is an automated notification from mySuccession.
                        </p>
                      </div>
                    </div>
                  </body>
                </html>
              `,
            },
          });
        } catch (error) {
          console.error('[Admin Notification] Failed to send:', error);
        }
      };
      sendAdminNotification(
        formData.firstName.trim(),
        formData.lastName.trim(),
        user.email || ''
      );

      await queryClient.invalidateQueries({ queryKey: ['auth-guard-profile-role', user.id] });

      // Save primary_goals to team_members table
      if (finalGoals.length > 0) {
        await supabase
          .from("team_members")
          .update({ primary_goals: finalGoals })
          .eq("user_id", user.id);
      }

      navigate("/dashboard", { replace: true });
    } catch (error: any) {
      console.error('Submit handler error:', error);
      console.error('RPC full error:', JSON.stringify(error, null, 2));
      console.error('RPC error message:', error?.message);
      console.error('RPC error code:', error?.code);
      console.error('RPC error details:', error?.details);
      console.error('RPC error hint:', error?.hint);
      Sentry.captureException(error, {
        tags: { context: "profile_error", operation: "complete_profile", component: "CompleteProfile" },
        extra: { message: "Profile completion error" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  const practiceNameLabel = formData.businessStructure === "sole_proprietorship" ? "DBA (Doing Business As)" : "Practice Name";

  const stepIndicator =
    currentStepIndex >= 0 ? (
      <p className="text-sm text-muted-foreground text-center">
        {stepNumber} of 6
      </p>
    ) : null;

  const backLink = (
    <div className="text-center">
      <button type="button" onClick={goBack} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
        Back
      </button>
    </div>
  );

  // ─── Welcome Screen ───
  if (screen === "welcome") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px] space-y-8">
          <div className="flex justify-center" />
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-bold text-foreground">Welcome to mySuccession</h1>
            <p className="text-sm text-muted-foreground">Let's set up your account. 6 quick steps while we build your dashboard.</p>
          </div>
          <Button className="w-full" onClick={() => goTo("name")}>
            Let's Go
          </Button>
        </div>
      </div>
    );
  }

  // ─── Completion Screen ───
  if (screen === "complete") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px] space-y-8">
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-bold text-foreground">You're all set.</h1>
            <p className="text-sm text-muted-foreground">Let's build your succession plan.</p>
          </div>
          <Button className="w-full" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Setting up…" : "Go to Dashboard"}
          </Button>
        </div>
      </div>
    );
  }

  // ─── Step Screens ───
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-[400px] space-y-8">
        {stepIndicator}

        {/* Screen 1 — Name */}
        {screen === "name" && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-foreground text-center">First, what's your name?</h1>
            <div className="space-y-4">
              <Input
                placeholder="First name"
                value={formData.firstName}
                onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                autoFocus
              />
              <Input
                placeholder="Last name"
                value={formData.lastName}
                onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
              />
            </div>
            <Button className="w-full" disabled={!canProceed()} onClick={goNext}>
              Continue
            </Button>
          </div>
        )}

        {/* Screen 2 — Role */}
        {screen === "role" && (
          <div className="space-y-6">
            <div className="space-y-1 text-center">
              <h1 className="text-2xl font-bold text-foreground">Which best describes you?</h1>
              <p className="text-sm text-muted-foreground">Select the option that best describes your relationship to the practice.</p>
            </div>
            <div className="space-y-3">
              <button
                type="button"
                className="w-full text-left rounded-md border-2 border-primary bg-primary/5 p-4 transition-colors"
                onClick={goNext}
              >
                <p className="font-medium text-foreground">Principal Advisor / Business Owner</p>
                <p className="text-sm text-muted-foreground mt-1">I am exploring the future transition of my practice.</p>
              </button>
              <div className="w-full text-left rounded-md border border-border p-4 opacity-50 cursor-not-allowed relative">
                <span className="absolute top-3 right-3 text-xs font-medium bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                  Coming Soon
                </span>
                <p className="font-medium text-muted-foreground">Potential Successor</p>
                <p className="text-sm text-muted-foreground mt-1">I am preparing to take over a practice.</p>
              </div>
              <div className="w-full text-left rounded-md border border-border p-4 opacity-50 cursor-not-allowed relative">
                <span className="absolute top-3 right-3 text-xs font-medium bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                  Coming Soon
                </span>
                <p className="font-medium text-muted-foreground">Consultant or Advisor</p>
                <p className="text-sm text-muted-foreground mt-1">I am supporting a practice through succession.</p>
              </div>
            </div>
            {backLink}
          </div>
        )}

        {/* Screen 3 — Practice (Business Structure + Province) */}
        {screen === "practice" && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-foreground text-center">Tell us about your practice.</h1>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Business Structure</Label>
                <Select
                  value={formData.businessStructure}
                  onValueChange={(v) => setFormData((p) => ({ ...p, businessStructure: v }))}
                >
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder="Select business structure" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_STRUCTURES.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Province <span className="text-destructive">*</span></Label>
                <p className="text-xs text-muted-foreground">Province where your practice is headquartered.</p>
                <Select value={formData.province} onValueChange={(v) => setFormData((p) => ({ ...p, province: v }))}>
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder="Select province" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVINCES.map((prov) => (
                      <SelectItem key={prov.code} value={prov.code}>
                        {prov.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button className="w-full" disabled={!canProceed()} onClick={goNext}>
              Continue
            </Button>
            {backLink}
          </div>
        )}

        {/* Screen 4 — Practice Details (NEW) */}
        {screen === "details" && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-foreground text-center">Practice Details</h1>
            <div className="space-y-5">
              {/* Practice Name */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">{practiceNameLabel}</Label>
                <Input
                  placeholder="Your practice name"
                  value={formData.practiceName}
                  onChange={(e) => setFormData((p) => ({ ...p, practiceName: e.target.value }))}
                  autoFocus
                />
              </div>

              {/* Registration Number — only for corporations */}
              {formData.businessStructure === "corporation" && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-foreground">Registration Number</Label>
                  <Input
                    placeholder="e.g. 1234567"
                    value={formData.registrationNumber}
                    onChange={(e) => {
                      setFormData((p) => ({ ...p, registrationNumber: e.target.value }));
                      if (registrationError) setRegistrationError("");
                    }}
                    onBlur={() => {
                      if (formData.registrationNumber.trim() && !validateRegistrationNumber(formData.registrationNumber)) {
                        setRegistrationError("Please enter a 7-digit corporation number or 10-digit NEQ");
                      }
                    }}
                  />
                  {registrationError && (
                    <p className="text-sm text-destructive">{registrationError}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    We only need your registry ID.{" "}
                    <a href="https://ised-isde.canada.ca/cc/lgcy/fdrlCrpSrch.html" target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80">
                      7-digit corporation number
                    </a>{" "}
                    or{" "}
                    <a href="https://www.quebec.ca/en/businesses-and-self-employed-workers/find-information-about-enterprise/search-enterprise-register/find-an-enterprise" target="_blank" rel="noopener noreferrer" className="underline text-primary hover:text-primary/80">
                      10-digit Quebec Enterprise Number (NEQ)
                    </a>
                    . If not known we can enter this later.
                  </p>
                </div>
              )}

              {/* Business Address */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-foreground">Business Address</Label>
                <Input
                  placeholder="Street Address"
                  value={formData.streetAddress}
                  onChange={(e) => setFormData((p) => ({ ...p, streetAddress: e.target.value }))}
                />
                <Input
                  placeholder="City"
                  value={formData.city}
                  onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))}
                />
                <Select
                  value={formData.addressProvince}
                  onValueChange={(v) => setFormData((p) => ({ ...p, addressProvince: v }))}
                >
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder="Province / Territory" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVINCES.map((prov) => (
                      <SelectItem key={prov.code} value={prov.code}>
                        {prov.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Postal Code (e.g. A1A 1A1)"
                  value={formData.postalCode}
                  onChange={(e) => {
                    setFormData((p) => ({ ...p, postalCode: e.target.value }));
                    if (postalCodeError) setPostalCodeError("");
                  }}
                  onBlur={() => {
                    if (formData.postalCode.trim() && !validatePostalCode(formData.postalCode)) {
                      setPostalCodeError("Please enter a valid Canadian postal code (e.g. A1A 1A1)");
                    }
                  }}
                />
                {postalCodeError && (
                  <p className="text-sm text-destructive">{postalCodeError}</p>
                )}
              </div>

              {/* Business Website */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Business Website</Label>
                <Input
                  placeholder="https://www.yourpractice.com"
                  value={formData.website}
                  onChange={(e) => setFormData((p) => ({ ...p, website: e.target.value }))}
                />
              </div>
            </div>
            <Button className="w-full" disabled={!canProceed()} onClick={goNext}>
              Continue
            </Button>
            {backLink}
          </div>
        )}

        {/* Screen 5 — Credentials */}
        {screen === "credentials" && (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-foreground text-center">Tell us about your advisory credentials.</h1>
            <div className="space-y-5">
              {/* Primary Firm or Sponsoring Affiliation — typeahead */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Primary Firm or Sponsoring Affiliation <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Popover open={firmSearchOpen} onOpenChange={setFirmSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={firmSearchOpen}
                      className="w-full justify-between font-normal"
                    >
                      {formData.primaryDealer || "Search or type firm name..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search firm..."
                        value={firmSearchValue}
                        onValueChange={setFirmSearchValue}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {firmSearchValue.trim() ? (
                            <button
                              type="button"
                              className="w-full px-4 py-2 text-left text-sm hover:bg-accent cursor-pointer"
                              onClick={() => {
                                setFormData((p) => ({ ...p, primaryDealer: firmSearchValue.trim() }));
                                setFirmSearchOpen(false);
                                setFirmSearchValue("");
                              }}
                            >
                              Use "{firmSearchValue.trim()}" as custom entry
                            </button>
                          ) : (
                            "Type to search..."
                          )}
                        </CommandEmpty>
                        {Object.entries(groupedAffiliations).map(([category, items]) => (
                          <CommandGroup key={category} heading={category}>
                            {items.map((firm) => (
                              <CommandItem
                                key={firm.name}
                                value={firm.name}
                                onSelect={() => {
                                  setFormData((p) => ({ ...p, primaryDealer: firm.name }));
                                  setFirmSearchOpen(false);
                                  setFirmSearchValue("");
                                }}
                              >
                                <Check
                                  className={cn("mr-2 h-4 w-4", formData.primaryDealer === firm.name ? "opacity-100" : "opacity-0")}
                                />
                                {firm.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        ))}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Regulatory License — dropdown */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">What regulatory licence do you hold?</Label>
                <Select
                  value={formData.regulatoryLicense}
                  onValueChange={(v) => setFormData((p) => ({ ...p, regulatoryLicense: v }))}
                >
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder="Select licence" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGULATORY_LICENSES.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Advisory Role — dropdown */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">What is your primary financial advisory role?</Label>
                <Select
                  value={formData.advisoryRole}
                  onValueChange={(v) => setFormData((p) => ({ ...p, advisoryRole: v }))}
                >
                  <SelectTrigger className="w-full bg-background">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ADVISORY_ROLES.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.advisoryRole === "Other" && (
                  <Input
                    placeholder="Enter your role..."
                    value={formData.otherRoleText}
                    onChange={(e) => setFormData((p) => ({ ...p, otherRoleText: e.target.value }))}
                    autoFocus
                    className="mt-2"
                  />
                )}
              </div>
            </div>
            <Button className="w-full" disabled={!canProceed()} onClick={goNext}>
              Continue
            </Button>
            {backLink}
          </div>
        )}

        {/* Screen 6 — Goals */}
        {screen === "goals" && (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold text-foreground">What brings you to mySuccession?</h1>
              <p className="text-sm text-muted-foreground">Select all that apply.</p>
            </div>
            <div className="space-y-3">
              {ADVISOR_PRIMARY_GOALS.map((goal) => (
                <div key={goal} className="flex items-center space-x-3">
                  <Checkbox
                    id={`goal-${goal}`}
                    checked={formData.goals.includes(goal)}
                    onCheckedChange={(checked) => handleGoalChange(goal, checked === true)}
                  />
                  <Label htmlFor={`goal-${goal}`} className="text-sm font-normal cursor-pointer">
                    {goal}
                  </Label>
                </div>
              ))}
              {formData.goals.includes("Other") && (
                <Input
                  placeholder="Tell us more..."
                  value={formData.otherGoalText}
                  onChange={(e) => setFormData((p) => ({ ...p, otherGoalText: e.target.value }))}
                  className="mt-2"
                />
              )}
            </div>
            <Button className="w-full" disabled={!canProceed()} onClick={goNext}>
              Complete Setup
            </Button>
            {backLink}
          </div>
        )}
      </div>
    </div>
  );
}
